import { AbstractControl, ValidationErrors } from '@angular/forms';

/**
 * Représente une valeur de message d'erreur.
 *
 * Deux cas sont possibles :
 * - une chaîne statique, par exemple : 'Champ obligatoire'
 * - une fonction, utile pour construire un message dynamique à partir
 *   des détails de l'erreur Angular (ex: maxlength.requiredLength)
 */
export type ErrorMessageValue =
  | string
  | ((error: any, control?: AbstractControl) => string);

/**
 * Associe une clé d'erreur Angular à un message.
 *
 * Exemple :
 * {
 *   required: 'Champ obligatoire',
 *   maxlength: (error) => `Maximum ${error.requiredLength} caractères`
 * }
 */
export type ControlErrorMessages = Record<string, ErrorMessageValue>;

/**
 * Retourne le premier message d'erreur trouvable pour un contrôle Angular.
 *
 * Fonctionnement :
 * - si le contrôle n'existe pas ou n'a pas d'erreurs, retourne une chaîne vide
 * - parcourt les erreurs du contrôle dans leur ordre de présence
 * - cherche un message correspondant dans la map `errorMessages`
 * - si le message est une fonction, l'exécute avec le détail de l'erreur
 * - sinon retourne directement la chaîne de texte
 *
 * @param control Contrôle Angular à analyser
 * @param errorMessages Map entre clés d'erreurs et messages associés
 * @returns Le premier message d'erreur à afficher, ou '' si aucun message ne correspond
 */
export function getFirstControlErrorMessage(
  control: AbstractControl | null,
  errorMessages: ControlErrorMessages,
): string {
  if (!control?.errors) {
    return '';
  }

  const errors: ValidationErrors = control.errors;

  for (const errorKey of Object.keys(errors)) {
    const resolver = errorMessages[errorKey];

    if (!resolver) {
      continue;
    }

    if (typeof resolver === 'function') {
      return resolver(errors[errorKey], control);
    }

    return resolver;
  }

  return '';
}
